import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import {
  BULK_CONTACT_COLUMN_LABELS,
  BULK_CONTACT_RAW_FIELDS,
  BULK_CONTACT_REQUIRED_COLUMNS,
  type BulkContactRawRow,
} from '../../types/contactBulkImport';

// Bulk Contact Upload — client-side file parsing. Extracts raw string rows
// from an .xlsx / .csv (or pasted CSV text) keyed by the spec's fixed column
// headers (§1.1). All cleanup/validation/resolution happens server-side; this
// layer only maps columns and drops blank rows.

export type RawRowsResult = {
  rows: BulkContactRawRow[];
  // Friendly labels of required columns the file is missing (header row). Non-
  // empty => the whole file is rejected before posting.
  missingRequiredColumns: string[];
};

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, ' ');
}

function cellToString(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

// Map the file's header row onto the raw-row field keys (case-insensitive,
// whitespace-collapsed against BULK_CONTACT_COLUMN_LABELS).
export function buildHeaderFieldMap(headers: string[]): {
  fieldToHeader: Partial<Record<keyof BulkContactRawRow, string>>;
  missingRequiredColumns: string[];
} {
  const headerByNormalized = new Map<string, string>();
  for (const h of headers) headerByNormalized.set(normalizeHeader(h), h);

  const fieldToHeader: Partial<Record<keyof BulkContactRawRow, string>> = {};
  for (const field of BULK_CONTACT_RAW_FIELDS) {
    const found = headerByNormalized.get(normalizeHeader(BULK_CONTACT_COLUMN_LABELS[field]));
    if (found !== undefined) fieldToHeader[field] = found;
  }

  const missingRequiredColumns = BULK_CONTACT_REQUIRED_COLUMNS.filter(
    (field) => !(field in fieldToHeader),
  ).map((field) => BULK_CONTACT_COLUMN_LABELS[field]);

  return { fieldToHeader, missingRequiredColumns };
}

// Pure: given the header list + per-row records (keyed by raw header), produce
// raw rows. Fully-blank rows are dropped. Exported for unit testing.
export function extractRawRows(
  headers: string[],
  records: Array<Record<string, unknown>>,
): RawRowsResult {
  const { fieldToHeader, missingRequiredColumns } = buildHeaderFieldMap(headers);
  if (missingRequiredColumns.length > 0) {
    return { rows: [], missingRequiredColumns };
  }

  const rows: BulkContactRawRow[] = [];
  for (const rec of records) {
    const row = {} as BulkContactRawRow;
    for (const field of BULK_CONTACT_RAW_FIELDS) {
      const header = fieldToHeader[field];
      row[field] = header ? cellToString(rec[header]) : '';
    }
    const anyValue = BULK_CONTACT_RAW_FIELDS.some((f) => row[f] !== '');
    if (anyValue) rows.push(row);
  }
  return { rows, missingRequiredColumns: [] };
}

function sheetToHeaderAndRecords(sheet: XLSX.WorkSheet): {
  headers: string[];
  records: Array<Record<string, unknown>>;
} {
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    blankrows: false,
  });
  if (aoa.length === 0) return { headers: [], records: [] };
  const headers = (aoa[0] ?? []).map((v) => cellToString(v));
  const records = aoa.slice(1).map((rowArr) => {
    const rec: Record<string, unknown> = {};
    headers.forEach((h, i) => {
      rec[h] = (rowArr as unknown[])[i];
    });
    return rec;
  });
  return { headers, records };
}

export function parseCsvText(text: string): RawRowsResult {
  const parsed = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    skipEmptyLines: true,
  });
  const headers = parsed.meta.fields ?? [];
  return extractRawRows(headers, parsed.data);
}

export async function parseFileToRawRows(file: File): Promise<RawRowsResult> {
  const isCsv = /\.csv$/i.test(file.name);
  if (isCsv) {
    return parseCsvText(await file.text());
  }
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { rows: [], missingRequiredColumns: [] };
  const { headers, records } = sheetToHeaderAndRecords(wb.Sheets[sheetName]!);
  return extractRawRows(headers, records);
}

// Build + download a blank .xlsx template with the §1.1 header row.
export function downloadBulkContactTemplate(): void {
  const headers = BULK_CONTACT_RAW_FIELDS.map((f) => BULK_CONTACT_COLUMN_LABELS[f]);
  const ws = XLSX.utils.aoa_to_sheet([headers]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
  XLSX.writeFile(wb, 'kyck-bulk-contact-template.xlsx');
}
